const SVG_NS = 'http://www.w3.org/2000/svg'

/** One booking lands, then availability goes back out to everywhere else. */
const CYCLE_MS = 3400
const IN_MS = 1150
const OUT_MS = 620
const OUT_STAGGER_MS = 45

const SEATS_START = 14
const SEATS_FLOOR = 4
const SEATS_RESET = 18

type Wire = {
  node: HTMLElement
  path: SVGPathElement
  length: number
}

type Hub = {
  stage: HTMLElement
  svg: SVGSVGElement
  disc: HTMLElement
  ring: HTMLElement
  seats: HTMLElement | null
  yours: HTMLElement
  wires: Wire[]
  dots: SVGCircleElement[]
  bigDot: SVGCircleElement
}

/**
 * Draws the channel hub's wires and runs its booking animation: a booking
 * arrives on one channel, then the new availability shoots back out to every
 * other channel at once.
 *
 * The wires are measured from the pills' real positions, so the same markup
 * works stacked (phone) or side by side (desktop) with no second diagram.
 */
export function initHub(): void {
  for (const root of document.querySelectorAll<HTMLElement>('[data-hub]')) {
    const hub = create(root)
    if (hub) run(hub)
  }
}

function create(root: HTMLElement): Hub | null {
  const stage = root.querySelector<HTMLElement>('.hub-stage')
  const svg = root.querySelector<SVGSVGElement>('.hub-wires')
  const disc = root.querySelector<HTMLElement>('.hub-disc')
  const ring = root.querySelector<HTMLElement>('.hub-ring')
  const yours = root.querySelector<HTMLElement>('.hub-yours')
  if (!stage || !svg || !disc || !ring || !yours) {
    throw new Error('A [data-hub] block is missing its stage, wires, disc, ring or yours wing.')
  }

  const nodes = [...root.querySelectorAll<HTMLElement>('.hub-node')]
  if (nodes.length === 0) return null

  const wires = nodes.map((node) => {
    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('class', 'hub-wire')
    svg.append(path)
    return { node, path, length: 0 }
  })

  // One dot per wire: the arriving booking reuses the first, the outgoing
  // availability updates use the rest.
  const dots = wires.map(() => {
    const dot = document.createElementNS(SVG_NS, 'circle')
    dot.setAttribute('class', 'hub-dot')
    dot.setAttribute('r', '3.5')
    dot.style.display = 'none'
    svg.append(dot)
    return dot
  })

  const bigDot = document.createElementNS(SVG_NS, 'circle')
  bigDot.setAttribute('class', 'hub-dot')
  bigDot.setAttribute('r', '6')
  bigDot.style.display = 'none'
  svg.append(bigDot)

  return {
    stage, svg, disc, ring, yours,
    seats: root.querySelector<HTMLElement>('[data-hub-seats]'),
    wires, dots, bigDot,
  }
}

/** Re-draws every wire from the current layout. */
function layout(hub: Hub): void {
  const stage = hub.stage.getBoundingClientRect()
  const disc = hub.disc.getBoundingClientRect()
  if (stage.width === 0 || disc.width === 0) return

  hub.svg.setAttribute('viewBox', `0 0 ${stage.width} ${stage.height}`)

  const cx = disc.left + disc.width / 2 - stage.left
  const cy = disc.top + disc.height / 2 - stage.top
  const radius = disc.width / 2

  // Ask the layout which way it fell rather than re-testing the breakpoint.
  const stacked = hub.yours.getBoundingClientRect().bottom <= disc.top + 1

  for (const wire of hub.wires) {
    const rect = wire.node.getBoundingClientRect()
    const above = rect.top < disc.top

    // Leave from the pill edge that faces the disc.
    const ax = (stacked ? rect.left + rect.width / 2 : above ? rect.right : rect.left) - stage.left
    const ay = (stacked ? (above ? rect.bottom : rect.top) : rect.top + rect.height / 2) - stage.top

    // Land radially on the disc's rim, which is what fans the wires out.
    const dx = ax - cx
    const dy = ay - cy
    const dist = Math.hypot(dx, dy) || 1
    const ex = cx + (dx / dist) * (radius + 2)
    const ey = cy + (dy / dist) * (radius + 2)

    // Leave the pill square-on, arrive at the disc along the radius.
    const d = stacked
      ? `M${ax},${ay} C${ax},${ay + (ey - ay) * 0.45} ${ex},${ey - (ey - ay) * 0.4} ${ex},${ey}`
      : `M${ax},${ay} C${ax + (ex - ax) * 0.45},${ay} ${ex - (ex - ax) * 0.4},${ey} ${ex},${ey}`

    wire.path.setAttribute('d', d)
    wire.length = wire.path.getTotalLength()
  }
}

function place(dot: SVGCircleElement, wire: Wire, t: number): void {
  const point = wire.path.getPointAtLength(wire.length * t)
  dot.setAttribute('cx', String(point.x))
  dot.setAttribute('cy', String(point.y))
  dot.style.display = ''
}

const easeInOut = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
const easeOut = (t: number): number => 1 - (1 - t) ** 3

function run(hub: Hub): void {
  layout(hub)

  // Pill widths shift as the fonts land, which moves every wire's start.
  document.fonts?.ready.then(() => layout(hub))
  new ResizeObserver(() => layout(hub)).observe(hub.stage)

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  let seats = SEATS_START
  let source = 0
  let started = 0
  let landed = false
  let frame: number | undefined

  function reset(): void {
    hub.bigDot.style.display = 'none'
    for (const dot of hub.dots) dot.style.display = 'none'
    for (const wire of hub.wires) wire.path.classList.remove('live')
    for (const wire of hub.wires) wire.node.classList.remove('lit')
  }

  /** The booking has arrived: tick the calendar down and pulse the disc. */
  function land(): void {
    landed = true
    seats = seats <= SEATS_FLOOR ? SEATS_RESET : seats - 1
    if (hub.seats) hub.seats.textContent = `${seats} seats left`
    hub.ring.classList.remove('go')
    // Reflow between remove and add so the pulse restarts every time.
    void hub.ring.offsetWidth
    hub.ring.classList.add('go')
  }

  function tick(now: number): void {
    frame = requestAnimationFrame(tick)

    const elapsed = now - started
    const inbound = hub.wires[source]
    if (!inbound) return

    if (elapsed < IN_MS) {
      // A booking travels in along one channel.
      place(hub.bigDot, inbound, easeInOut(elapsed / IN_MS))
      inbound.path.classList.add('live')
      return
    }

    if (!landed) land()
    hub.bigDot.style.display = 'none'

    // ...and the new availability goes straight back out to all the others.
    let done = true
    for (const [i, wire] of hub.wires.entries()) {
      const dot = hub.dots[i]
      if (!dot || i === source) continue

      const t = (elapsed - IN_MS - i * OUT_STAGGER_MS) / OUT_MS
      if (t < 0) { done = false; continue }
      if (t >= 1) {
        dot.style.display = 'none'
        wire.node.classList.add('lit')
        continue
      }
      done = false
      place(dot, wire, 1 - easeOut(t))
    }

    if (done && elapsed >= CYCLE_MS) {
      reset()
      source = (source + 1) % hub.wires.length
      started = now
      landed = false
    }
  }

  // Only animate while the diagram is actually on screen.
  new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && frame === undefined) {
        layout(hub)
        started = performance.now()
        landed = false
        frame = requestAnimationFrame(tick)
      } else if (!entry.isIntersecting && frame !== undefined) {
        cancelAnimationFrame(frame)
        frame = undefined
        reset()
      }
    }
  }).observe(hub.stage)
}
